import { useEffect, useState } from "react";
import { Card, Form, Radio, Row, Col, Input, Button } from "antd";
import { useParams, useOutletContext } from "react-router-dom";
import lookupService from "../../../services/lookupService";
import eventsService from "../../../services/eventsService";
import { useOfflineMutationQueue } from "../../../hooks/useOfflineMutationQueue";
import { OFFLINE_FACILITATOR_CAPTURE_QUEUE_KEY } from "../../../hooks/offlineQueueConfig";

import { LocationListForm, LocationAccessControl } from "@seims/shared";

import { openCaptureNotification } from "../../custom/CaptureNotifications"
import { Notify } from "../../../services/custom/Messages";
import { recordError } from "../../../utils/logService";

import {
  QUALIFICATIONS_STORE_NAME,
  PARTICIPANT_OCCUPATIONS_STORE_NAME,
  EMPLOYMENT_SECTORS_STORE_NAME,
  EVENT_ROLES_STORE_NAME,
  ATTENDEE_MOTIVATIONS_STORE_NAME,
  LANGUAGES_STORE_NAME,
  TARGET_PARTICIPANTS_STORE_NAME,
  PARTICIPANT_DISABILITIES_STORE_NAME,
  GENDERS_STORE_NAME,
  RACES_STORE_NAME,
  AGE_GROUPS_STORE_NAME,
  PROVINCES_DISTRICTS_TOWNS_STORE_NAME,
} from "../../../constants"

const initialState = {
  qualification: null,
  occupation: null,
  sector: null,
  eventRole: null,
  motivation: null,
  language: null,
  participantType: null,
  disabilityTypes: [],
  gender: null,
  race: null,
  ageGroup: null,
}

export const ScienceFacilitatorsCapturing = () => {
  const [form] = Form.useForm();
  const { eventId } = useParams();
  const { coords, setOnlineCount } = useOutletContext();

  // Lookups
  const [qualifications, setQualifications] = useState([]);
  const [occupations, setOccupations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [eventRole, setEventRole] = useState([]);
  const [motivation, setMotivation] = useState([]);
  const [languages, setLanguages] = useState([]);

  const [participantTypes, setParticipantTypes] = useState([]);
  const [disabilityTypes, setDisabilityTypes] = useState([]);
  const [genders, setGenders] = useState([]);
  const [races, setRaces] = useState([]);
  const [ageGroups, setAgeGroups] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [provinces, setProvinces] = useState([]);

  const [showErrors, setShowErrors] = useState(false);

  const { runWithOfflineFallback } = useOfflineMutationQueue({
    storageKey: OFFLINE_FACILITATOR_CAPTURE_QUEUE_KEY,

    onFlush: async ({ syncedCount, failedCount }) => {
      if (syncedCount > 0) {
        openCaptureNotification(
          "Science Facilitator",
          `${syncedCount} queued facilitator${syncedCount > 1 ? "s" : ""} synced successfully`,
          Notify.SUCCESS
        );
      }

      if (failedCount > 0) {
        openCaptureNotification(
          "Science Facilitator",
          `${failedCount} queued facilitator${failedCount > 1 ? "s" : ""} failed to sync`,
          Notify.ERROR
        );
      }
    },
  });

  // Selected values
  const [selected, setSelected] = useState({
    qualification: null,
    occupation: null,
    sector: null,
    eventRole: null,
    motivation: null,
    language: null,
    participantType: null,
    disabilityTypes: [],
    gender: null,
    race: null,
    ageGroup: null,
  });

  useEffect(() => {
    loadLookups();
  }, []);

  const loadLookups = async () => {
    try {
      const [
        qualificationRes,
        occupationRes,
        sectorRes,
        eventRoleRes,
        motivationRes,
        languagesRes,
        participantResp,
        disabilityResp,
        genderResp,
        raceResp,
        ageGroupResp,
        provinceResp,
      ] = await Promise.all([
        lookupService.getCahedLookup(QUALIFICATIONS_STORE_NAME),
        lookupService.getCahedLookup(PARTICIPANT_OCCUPATIONS_STORE_NAME),
        lookupService.getCahedLookup(EMPLOYMENT_SECTORS_STORE_NAME),
        lookupService.getCahedLookup(EVENT_ROLES_STORE_NAME),
        lookupService.getCahedLookup(ATTENDEE_MOTIVATIONS_STORE_NAME),
        lookupService.getCahedLookup(LANGUAGES_STORE_NAME),
        lookupService.getCahedLookup(TARGET_PARTICIPANTS_STORE_NAME),
        lookupService.getCahedLookup(PARTICIPANT_DISABILITIES_STORE_NAME),
        lookupService.getCahedLookup(GENDERS_STORE_NAME),
        lookupService.getCahedLookup(RACES_STORE_NAME),
        lookupService.getCahedLookup(AGE_GROUPS_STORE_NAME),
        lookupService.getCahedLookup(PROVINCES_DISTRICTS_TOWNS_STORE_NAME),
      ]);

      setQualifications(qualificationRes?.data ?? []);
      setOccupations(occupationRes?.data ?? []);
      setSectors(sectorRes?.data ?? []);
      setEventRole(eventRoleRes?.data ?? []);
      setMotivation(motivationRes?.data ?? []);
      setLanguages(languagesRes?.data ?? []);
      setParticipantTypes(participantResp?.data ?? []);
      setDisabilityTypes(disabilityResp?.data ?? []);
      setGenders(genderResp?.data ?? []);
      setRaces(raceResp?.data ?? []);
      setAgeGroups(ageGroupResp?.data ?? []);
      setProvinces(provinceResp?.data ?? []);
    } catch (error) {
      console.error("Failed to load science facilitator lookups", error);
    }
  };

  const NONE_DISABILITY_KEY = disabilityTypes.find(
    (d) => d.value?.toLowerCase() === "none"
  )?.key;


  const renderGrid = (items, selectedKey, onSelect) => (
    <div
      className="tile-grid capture-attendee-columns"
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        rowGap: 14,
        columnGap:12
      }}
    >
      {items.map(item => (
        <div
          key={item.key}
          className={`tile ${selectedKey === item.key ? "tile--active" : ""
            }`}
          onClick={() => onSelect(item.key)}
        >
          {item.value}
        </div>
      ))}
    </div>
  );

  const onFinish = async (values) => {

    if (submitting) return;
    setSubmitting(true);

    setShowErrors(true);

    const errors = [];

    // Tile validations
    if (!selected.qualification) errors.push("Qualification is required");
    if (!selected.occupation) errors.push("Occupation is required");
    if (!selected.sector) errors.push("Employment sector is required");
    if (!selected.race) errors.push("Race is required");

    if (!selected.eventRole) errors.push("Event Role is required");
    if (!selected.motivation) errors.push("Motivation is required");
    if (!selected.language) errors.push("Language is required");

    if (!selected.participantType) errors.push("Attendee Type is required");
    if (!selected.gender) errors.push("Gender is required");
    if (!selected.ageGroup) errors.push("Age Group is required");
    if (selected.disabilityTypes.length === 0)
      errors.push("At least one Disability Type is required");

    if (!values.province) errors.push("Province is required");
    if (!values.district) errors.push("District is required");

    // Form validations
    if (!values.firstName) errors.push("First Name is required");
    if (!values.lastName) errors.push("Last Name is required");
    if (!values.specialisation)
      errors.push("Area of Specialisation is required");

    if (values.isRepeatScienceFacilitator === undefined)
      errors.push("Please indicate if this is a repeat science facilitator");

    if (
      values.email &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)
    ) {
      errors.push("Please enter a valid email address");
    }

    if (errors.length > 0) {
      //errors.forEach((msg) => message.error(msg));
      setSubmitting(false);
      return;
    }

    //Science facilitator payload
    const facilitatorPayload = {
      emailAddress: values.email || null,
      firstname: values.firstName,
      lastname: values.lastName,
      employmentSector: selected.sector,
      eventRole: selected.eventRole,
      language: selected.language,
      // Hard‑coded location for now
      location: {
        x: coords.latitude,
        y: coords.longitude,
      },
      motivation: selected.motivation,
      occupation: selected.occupation,
      qualification: selected.qualification,
      specialisation: values.specialisation,
      repeat: values.isRepeatScienceFacilitator,
      recordDate: new Date().toISOString(),
    };

    //Individual payload
    const attendancePayload = {
      type: selected.participantType,
      province: values.province,
      district: values.district,
      disabilities: selected.disabilityTypes,
      gender: selected.gender,
      race: selected.race,
      ageGroup: selected.ageGroup,
      repeat: values.isRepeatScienceFacilitator,
      recordDate: new Date().toISOString(),
      // Hard‑coded location for now
      location: {
        x: 0.1,
        y: 0.1,
      },
    };

    try {
            // Run both requests at the same time
      const { queued, response } = await runWithOfflineFallback(
        {
          eventId,
          attendee: attendancePayload,
          facilitator: facilitatorPayload,
        },
        { count: 1 }
      );

      if (queued) {
        openCaptureNotification(
          "Science Facilitator",
          "You are offline. Capture was queued and will sync when online",
          Notify.INFO
        );
      // Reset form and all tile selections
        form.resetFields();
        setSelected({ ...initialState });
        setShowErrors(false);
        return;
      }

      openCaptureNotification(
        "Science Facilitator",
        "Science facilitator added successfully",
        Notify.SUCCESS
      );

      // Successful online individual capture returns total count
      const totalCount = response?.find(res => typeof res?.data?.total === 'number')?.data?.total ?? 0;
      if (totalCount > 0) {
        setOnlineCount(totalCount)
      }

      form.resetFields();
      setSelected({ ...initialState });
      setShowErrors(false);

    } catch (error) {
      recordError(error);
      openCaptureNotification(
        "Science Facilitator",
        "Failed to add science facilitator",
        Notify.ERROR
      );

    } finally {
      setSubmitting(false);
    }
  };

  return (
    <LocationAccessControl>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        onFinishFailed={(errorInfo) => {
          console.log(" onFinishFailed", errorInfo);
          setShowErrors(true);
        }}
      >
        <Card style={{ borderRadius: 0, marginTop: 0 }} className="capture-attendees">
          {/*  Demographics */}
          <Card type="inner" title="Science Facilitator Details" className="event-card-inner " style={{ marginBottom: 8 }}>
            <Row gutter={[16, 8]}>
              {/* Email (optional) – full width */}
              <Col xs={24}>
                <Form.Item
                  label="Email (optional)"
                  name="email"
                  rules={[
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input placeholder="Enter email address" />
                </Form.Item>
              </Col>

              {/* First Name – 50% on desktop */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: "First name is required" },
                  ]}
                >
                  <Input placeholder="Enter first name" />
                </Form.Item>
              </Col>

              {/* Last Name – 50% on desktop */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: "Last name is required" },
                  ]}
                >
                  <Input placeholder="Enter last name" />
                </Form.Item>
              </Col>

              {/* Area of Specialisation – full width */}
              <Col xs={24}>
                <Form.Item
                  label="Area of Specialisation"
                  name="specialisation"
                  rules={[
                    {
                      required: true,
                      message: "Area of specialisation is required",
                    },
                  ]}
                >
                  <Input placeholder="Enter area of specialisation" />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Qualification */}
          <Card type="inner" title="Qualification" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(qualifications, selected.qualification, (key) =>
              setSelected((s) => ({ ...s, qualification: key }))
            )}
            {showErrors && !selected.qualification && (
              <div style={{ color: "red", marginTop: 4 }}>
                Qualification is required
              </div>
            )}
          </Card>


          {/* Occupation */}
          <Card type="inner" title="Occupation" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(occupations, selected.occupation, (key) =>
              setSelected((s) => ({ ...s, occupation: key }))
            )}
            {showErrors && !selected.occupation && (
              <div style={{ color: "red", marginTop: 4 }}>
                Occupation is required
              </div>
            )}
          </Card>

          {/* Employment Sector */}
          <Card type="inner" title="Employment Sector" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(sectors, selected.sector, (key) =>
              setSelected((s) => ({ ...s, sector: key }))
            )}
            {showErrors && !selected.sector && (
              <div style={{ color: "red", marginTop: 4 }}>
                Sector is required
              </div>
            )}
          </Card>

          {/* Event Role */}
          <Card type="inner" title="Event Role" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(eventRole, selected.eventRole, (key) =>
              setSelected((s) => ({ ...s, eventRole: key }))
            )}
            {showErrors && !selected.eventRole && (
              <div style={{ color: "red", marginTop: 4 }}>
                Role is required
              </div>
            )}
          </Card>

          {/* Attendee Motivation */}
          <Card type="inner" title="Attendee Motivation" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(motivation, selected.motivation, (key) =>
              setSelected((s) => ({ ...s, motivation: key }))
            )}
            {showErrors && !selected.motivation && (
              <div style={{ color: "red", marginTop: 4 }}>
                Motivation is required
              </div>
            )}
          </Card>

          {/* Facilitator Language */}
          <Card type="inner" title="Facilitator Language" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(languages, selected.language, (key) =>
              setSelected((s) => ({ ...s, language: key }))
            )}
            {showErrors && !selected.language && (
              <div style={{ color: "red", marginTop: 4 }}>
                Language is required
              </div>
            )}
          </Card>

          {/* Attendee Type */}
          <Card type="inner" title="Attendee Type" className="event-card-inner" style={{ marginBottom: 8 }}>
            {renderGrid(participantTypes, selected.participantType, (key) =>
              setSelected((s) => ({ ...s, participantType: key }))
            )}
            {showErrors && !selected.participantType && (
              <div style={{ color: "red", marginTop: 4 }}>
                Attendee Type is required
              </div>
            )}

          </Card>

          {/*  Location Form List */}

          <div className="location-compact">
            <LocationListForm
              form={form}
              provinces={provinces}
            />
          </div>

          {showErrors && !form.getFieldValue("province") && (
            <div style={{ color: "red", marginTop: 4 }}>
              Province is required
            </div>
          )}

          {showErrors && !form.getFieldValue("district") && (
            <div style={{ color: "red", marginTop: 4 }}>
              District is required
            </div>
          )}

          {/* Disability */}
          <Card type="inner" className="event-card-inner" title="Disability (Select all that apply)" style={{ marginBottom: 8 }}>
            <div
            className = "capture-attendee-columns"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                rowGap: 14,
                columnGap:12
              }}
            >
              {disabilityTypes.map((item) => (
                <div
                  key={item.key}
                  className={`tile ${selected.disabilityTypes.includes(item.key)
                    ? "tile--active"
                    : ""
                    }`}
                  onClick={() => {
                    setSelected((s) => {
                      const key = item.key;

                      // If "None" is clicked → clear all others
                      if (key === NONE_DISABILITY_KEY) {
                        return {
                          ...s,
                          disabilityTypes: [NONE_DISABILITY_KEY],
                        };
                      }

                      //  If another option is clicked → remove "None"
                      const withoutNone = s.disabilityTypes.filter(
                        (k) => k !== NONE_DISABILITY_KEY
                      );

                      const next = withoutNone.includes(key)
                        ? withoutNone.filter((k) => k !== key)
                        : [...withoutNone, key];

                      return {
                        ...s,
                        disabilityTypes: next,
                      };
                    });
                  }}
                >
                  {item.value}
                </div>
              ))}
              {showErrors && selected.disabilityTypes.length === 0 && (
                <div style={{ color: "red", marginTop: 4 }}>
                  At least one Disability Type is required
                </div>
              )}
            </div>

          </Card>

          {/* Gender */}
          <Card type="inner" className="event-card-inner" title="Gender" style={{ marginBottom: 8 }}>
            {renderGrid(genders, selected.gender, (key) =>
              setSelected((s) => ({ ...s, gender: key }))
            )}
            {showErrors && !selected.gender && (
              <div style={{ color: "red", marginTop: 4 }}>
                Gender is required
              </div>
            )}

          </Card>

          {/* Race */}
          <Card type="inner" className="event-card-inner" title="Race" style={{ marginBottom: 8 }}>
            {renderGrid(races, selected.race, (key) =>
              setSelected((s) => ({ ...s, race: key }))
            )}
            {showErrors && !selected.race && (
              <div style={{ color: "red", marginTop: 4 }}>
                At least one Race is required
              </div>
            )}
          </Card>

          {/* Age Group */}
          <Card type="inner" className="event-card-inner" title="Age Group" style={{ marginBottom: 8 }}>
            {renderGrid(ageGroups, selected.ageGroup, (key) =>
              setSelected((s) => ({ ...s, ageGroup: key }))
            )}
            {showErrors && !selected.ageGroup && (
              <div style={{ color: "red", marginTop: 4 }}>
                Age group is required
              </div>
            )}

          </Card>

          {/*  Repeat Science Facilitator */}
          <Card type="inner" className="event-card-inner repeat-attendee-card" title="Repeat Science Facilitator" style={{ marginBottom: 8 }}>
            <Form.Item
              name="isRepeatScienceFacilitator"
              // label={
              //   <span style={{ fontWeight: 600 }}>
              //     Is this a repeat science facilitator?
              //   </span>
              // }
              rules={[
                { required: true, message: "Please select an option" },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
          </Card>
          <div className="footer-holder">
          <Button
          className="group-footer"
          type="primary"
          htmlType="submit"
          block
          style={{
            background: "#1F7AB6",
            borderRadius: 20,
            padding: "6px 28px",
            fontWeight: 500,
            border: "none",
          }}
              loading={submitting}
              disabled={submitting}
        >
          + Add Facilitator
        </Button>
        </div>
        <br/><br/>
        </Card>
      </Form >
    </LocationAccessControl>
  );
};